import * as packagesService from "../services/packagesService";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export const usePackagesInfiniteQuery = (params) => {
  const searchParams = new URLSearchParams(params);
  return useInfiniteQuery({
    queryKey: ["packages", params],
    queryFn: async ({ pageParam }) => {
      return packagesService.fetchPackages(
        `page=${pageParam}&${searchParams.toString()}`
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.nextPage !== null
        ? lastPage.pageInfo.nextPage
        : undefined;
    },
  });
};

export const usePackageByIdQuery = (id) => {
  return useQuery({
    queryKey: ["package", id],
    queryFn: () => packagesService.fetchPackageById(id),
    retry: 1,
  });
};

export const useAddTrackingNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, values }) =>
      packagesService.addTrackingNumber(id, values.trackingNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (error) => {
      console.error("Failed to add tracking number:", error);
    },
  });
};

export const useExportPackages = (filter) => {
  const searchParams = new URLSearchParams(filter);

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const blob = await packagesService.exportPackages(
        searchParams.toString()
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "skypostal-packages.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["files"] });
    },
    onError: (error) => {
      console.error("Failed to exports packages:", error);
    },
  });
};

export const usePackagesImportedInfiniteQuery = (params) => {
  const searchParams = new URLSearchParams(params);
  return useInfiniteQuery({
    queryKey: ["packagesImported", params],
    queryFn: async ({ pageParam }) => {
      return packagesService.fetchPackagesImported(
        `page=${pageParam}&${searchParams.toString()}`
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.nextPage !== null
        ? lastPage.pageInfo.nextPage
        : undefined;
    },
  });
};

export const usePackagesImportedUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, values }) =>
      packagesService.updateImportedPackage(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packagesImported"] });
    },
    onError: (error) => {
      console.error("Failed to update package status:", error);
    },
  });
};

export const useDeleteImportedPackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => packagesService.deleteImportedPackage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packagesImported"] });
    },
    onError: (error) => {
      console.error("Failed to delete imported package:", error);
    },
  });
};

export const useSaveToPackages = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => packagesService.saveToPackages(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["packagesImported"],
      });
    },
    onError: (error) => {
      console.error("Failed to save imported package:", error);
    },
  });
};

export const useCreateValidPackagesFromImported = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => packagesService.createValidPackagesFromImported(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["packagesImported"],
      });
    },
    onError: (error) => {
      console.error("Failed to create valid packages from imported:", error);
    },
  });
};

export const useDeletePackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => packagesService.deletePackage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (error) => {
      console.error("Failed to delete package:", error);
    },
  });
};

export const useEditPackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, values }) => packagesService.updatePackage(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (error) => {
      console.error("Failed to update package status:", error);
    },
  });
};

// Main usePackages hook for backwards compatibility
export const usePackages = (query) => {
  const queryClient = useQueryClient();

  if (!query) {
    query = {
      page: 1,
      limit: 10,
      query: {},
    };
  }

  const { data: packages, isLoading } = useQuery({
    queryKey: ["packages", query],
    queryFn: () => packagesService.fetchPackages(""),
  });

  const createMutation = useMutation({
    mutationFn: packagesService.createPackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }) => packagesService.updatePackage(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packages", "package"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: packagesService.deletePackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
  });

  return {
    packages,
    isLoading,
    createPackage: createMutation.mutate,
    updatePackage: updateMutation.mutate,
    deletePackage: deleteMutation.mutate,
    createPackageLoading: createMutation.isPending,
    updatePackageLoading: updateMutation.isPending,
    deletePackageLoading: deleteMutation.isPending,
  };
};
